import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";
import { FIELD_LABEL_CLASS } from "./input";

// Cohort spec `.ta`: flat bordered textarea — 8px/10px padding, 1px outline
// border, radius r2, focus = primary border + 2px primary/20 ring, vertical
// resize. No floating label — `label` renders as a plain block label above.
const textareaVariants = cva(
  [
    "block w-full resize-y rounded-lg border border-outline bg-background px-2.5 py-2 type-body-m text-foreground",
    "placeholder:text-on-surface-variant/60 placeholder:font-normal",
    "transition-[border-color,box-shadow,background-color] duration-[var(--duration-short-3)] ease-[var(--ease-emphasized)] motion-reduce:transition-none",
    "hover:border-foreground",
    "focus-visible:outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20",
    "disabled:bg-muted disabled:text-foreground/60 disabled:border-outline-variant disabled:cursor-not-allowed disabled:hover:border-outline-variant",
    "aria-invalid:border-error aria-invalid:hover:border-error aria-invalid:focus-visible:border-error aria-invalid:focus-visible:ring-error/20",
  ],
  {
    variants: {
      variant: {
        default: "",
        primary: "",
        secondary: "",
        info: "",
        success: "",
        warning: "",
        error: "border-error hover:border-error",
      },
      inputSize: {
        default: "min-h-[88px]",
        sm: "min-h-[64px] type-body-s",
        lg: "min-h-[120px] type-body-l",
      },
    },
    defaultVariants: {
      variant: "default",
      inputSize: "default",
    },
  },
);

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    VariantProps<typeof textareaVariants> {
  label?: string;
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      variant,
      inputSize,
      label,
      placeholder,
      id: idProp,
      "aria-invalid": ariaInvalidProp,
      ...props
    },
    ref,
  ) => {
    const reactId = React.useId();
    const textareaId = idProp !== undefined ? idProp : reactId;

    const ariaInvalid = ariaInvalidProp !== undefined
      ? ariaInvalidProp
      : variant === "error"
        ? true
        : undefined;

    const hasLabel = label !== undefined && label !== "";

    const textareaElement = (
      <textarea
        ref={ref}
        id={textareaId}
        aria-invalid={ariaInvalid}
        placeholder={placeholder}
        className={cn(textareaVariants({ variant, inputSize }), className)}
        {...props}
      />
    );

    if (hasLabel === false) {
      return textareaElement;
    }

    return (
      <div className="flex w-full flex-col gap-1.5">
        <label htmlFor={textareaId} className={FIELD_LABEL_CLASS}>
          {label}
        </label>
        {textareaElement}
      </div>
    );
  },
);

Textarea.displayName = "Textarea";

export { Textarea };
